import type { MouseEvent } from 'react';
import { styled } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link as RouterLink, navigate } from '@reach/router';
import type { AdminPolicyStreamPlanTableFieldsFragment } from '../../../gql/graphql';
import { formatDateTimeForDisplay, truncateMiddle } from '../../../util';

const QUOTE_DETAILS_PATH = '/insurtech/manager/quotes/details';
const COMPULIFE_CALLS_BASE = '/insurtech/admin/policystream/compulifecalls';

export type AdminPolicyStreamPlanTableRow =
  AdminPolicyStreamPlanTableFieldsFragment;

interface AdminPolicyStreamPlansTableProps {
  plans: Array<AdminPolicyStreamPlanTableRow | null | undefined>;
  /** When set, clicking a row navigates here (inner links stop propagation). */
  planDetailPathForId?: (planId: string) => string;
}

export const AdminPolicyStreamPlansTable = ({
  plans,
  planDetailPathForId,
}: AdminPolicyStreamPlansTableProps) => {
  return (
    <ScrollTableContainer>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            {[
              'ID',
              'Created At',
              'Coverage Amount (Dollars)',
              'Markdown Details',
              'Premium Annual (Dollars)',
              'Premium Monthly (Dollars)',
              'Premium Quarterly (Dollars)',
              'Term Length',
              'Term Length Description',
              'Insurer',
              'Quote',
              'Archived Quote',
              'Compulife API Call',
            ].map((header) => {
              return <HeaderCell key={header}>{header}</HeaderCell>;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {plans.map((plan) => {
            return plan ? (
              <PlanRow
                key={plan.id}
                plan={plan}
                planDetailPathForId={planDetailPathForId}
              />
            ) : null;
          })}
        </TableBody>
      </Table>
    </ScrollTableContainer>
  );
};

interface PlanRowProps {
  plan: AdminPolicyStreamPlanTableRow;
  planDetailPathForId?: (planId: string) => string;
}

const PlanRow = ({ plan, planDetailPathForId }: PlanRowProps) => {
  const handleRowClick = () => {
    if (planDetailPathForId) {
      navigate(planDetailPathForId(plan.id));
    }
  };

  return (
    <BodyRow
      $clickable={Boolean(planDetailPathForId)}
      onClick={planDetailPathForId ? handleRowClick : undefined}
    >
      <BodyCell>{plan.id}</BodyCell>
      <BodyCell>{formatDateTimeForDisplay(plan.createdAt)}</BodyCell>
      <BodyCell>{plan.coverageAmountDollars ?? '—'}</BodyCell>
      <MarkdownCell title={plan.markdownDetails}>
        {truncateMiddle(plan.markdownDetails, 80)}
      </MarkdownCell>
      <BodyCell>{plan.premiumAnnualDollars}</BodyCell>
      <BodyCell>{plan.premiumMonthlyDollars}</BodyCell>
      <BodyCell>{plan.premiumQuarterlyDollars}</BodyCell>
      <BodyCell>
        {plan.termLength !== null && plan.termLength !== undefined
          ? String(plan.termLength)
          : '—'}
      </BodyCell>
      <BodyCell>{plan.termLengthDescription ?? '—'}</BodyCell>
      <BodyCell>{plan.insurer?.displayName ?? '—'}</BodyCell>
      <BodyCell onClick={stopRowNavigation}>
        {plan.quote?.uniqueID ? (
          <TableRouterLink
            to={`${QUOTE_DETAILS_PATH}/${String(plan.quote.uniqueID)}`}
            onClick={stopRowNavigation}
          >
            {String(plan.quote.uniqueID)}
          </TableRouterLink>
        ) : (
          '—'
        )}
      </BodyCell>
      <BodyCell onClick={stopRowNavigation}>
        {plan.archivedQuote?.uniqueID ? (
          <TableRouterLink
            to={`${QUOTE_DETAILS_PATH}/${String(plan.archivedQuote.uniqueID)}`}
            onClick={stopRowNavigation}
          >
            {String(plan.archivedQuote.uniqueID)}
          </TableRouterLink>
        ) : (
          '—'
        )}
      </BodyCell>
      <BodyCell onClick={stopRowNavigation}>
        {plan.compulifeApiCall?.id ? (
          <TableRouterLink
            to={`${COMPULIFE_CALLS_BASE}/${plan.compulifeApiCall.id}`}
            onClick={stopRowNavigation}
          >
            {plan.compulifeApiCall.id}
          </TableRouterLink>
        ) : (
          '—'
        )}
      </BodyCell>
    </BodyRow>
  );
};

const stopRowNavigation = (event: MouseEvent) => {
  event.stopPropagation();
};

const ScrollTableContainer = styled(TableContainer)(({ theme }) => {
  return {
    maxWidth: '100%',
    overflowX: 'auto',
    marginTop: theme.spacing(2),
  };
});

const HeaderCell = styled(TableCell)(() => {
  return {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
});

interface BodyRowProps {
  $clickable: boolean;
}

const BodyRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== '$clickable',
})<BodyRowProps>(({ theme, $clickable }) => {
  return {
    ...($clickable
      ? {
          cursor: 'pointer',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }
      : {}),
  };
});

export const TableRouterLink = styled(RouterLink)(({ theme }) => {
  return {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': { textDecoration: 'underline' },
  };
});

const BodyCell = styled(TableCell)(() => {
  return {
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  };
});

const MarkdownCell = styled(BodyCell)(() => {
  return {
    whiteSpace: 'normal',
    maxWidth: 280,
    wordBreak: 'break-word',
  };
});
